import { Model, ModelAttributes, collection, model, modelConfig, reference } from "@mixitone/oom";
import { clearMemoized, isNil, makeRecordSetRelationshipsBidirectional, memoize } from "@mixitone/util";
import Color from "color";
import AccountUser from "./AccountUser";
import ClubPlayer, { type ClubPlayerMemberType } from "./ClubPlayer";
import ClubDocument from "./ClubDocument";
import FillRule, { RuleType } from "./FillRule";
import Funder from "./Funder";
import FundingAppeal from "./FundingAppeal";
import FundingOutreach from "./FundingOutreach";
import Group, { defaultColors } from "./Group";
import Night from "./Night";
import { PermissionKey } from "./ClubPermission";
import Account from "./Account";
import ClubMessage from "./ClubMessage";
import { Database } from "./database.types";

export type ClubPrintPaletteId = "teal" | "slateYellow" | "blackOrange" | "navySky" | "forest";

export interface ClubPreferences {
  enableQrScanning?: boolean;
  hasMembers?: boolean;
  hasCasual?: boolean;
  printPalette?: ClubPrintPaletteId;
  memberRequirements?: {
    email?: boolean;
    phone?: boolean;
    acceptConstitution?: boolean;
  };
}

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    address: {
      type: "string",
    },
    contact_id: {
      type: "string",
    },
    country: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    directory_index_visible: {
      type: "boolean",
    },
    directory_slug: {
      type: "string",
    },
    directory_update_count: {
      type: "number",
    },
    email: {
      type: "string",
    },
    enable_directory_contact: {
      type: "boolean",
    },
    facebook_page: {
      type: "string",
    },
    is_deleted: {
      type: "boolean",
    },
    is_temporary: {
      type: "boolean",
    },
    last_directory_update_at: {
      type: "datetime",
    },
    listed_in_directory: {
      type: "boolean",
    },
    name: {
      type: "string",
    },
    preferences: {
      type: "json",
      jsonType: {} as ClubPreferences,
    },
    publish_session_stats_in_directory: {
      type: "boolean",
    },
    region: {
      type: "string",
    },
    sport_type: {
      type: "enum",
      enumType: "" as Database["public"]["Enums"]["club_sport_type"],
    },
    telephone: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
    webpage: {
      type: "string",
    },
  },
  {
    club_players: collection<ClubPlayer>(() => ClubPlayer, "club_id"),
    groups: collection<Group>(() => Group, "club_id", { order: "index" }),
    fill_rules: collection<FillRule>(() => FillRule, "club_id", { order: "created_at" }),
    club_documents: collection<ClubDocument>(() => ClubDocument, "club_id"),
    funders: collection(() => Funder as any, "club_id"),
    funding_appeals: collection(() => FundingAppeal as any, "club_id"),
    funding_outreach: collection(() => FundingOutreach as any, "club_id"),
    club_messages: collection<ClubMessage>(() => ClubMessage, "club_id", { order: "created_at" }),
  },
  {
    contact: reference(() => AccountUser, "contact_id"),
  },
);

interface Club extends ModelAttributes<typeof config> {}

@model("clubs", config)
class Club extends Model<typeof config> {
  override canEdit() {
    return AccountUser.current?.role === "Admin" || AccountUser.current?.id === this.contact_id;
  }

  async ensureGroups() {
    await this.groups.load();

    if (this.groups.length === 0) {
      const bGroup = new Group({
        account_id: this.account_id,
        club_id: this.id,
        name: "Beginner",
        index: 0,
      });
      const iGroup = new Group({
        account_id: this.account_id,
        club_id: this.id,
        name: "Intermediate",
        index: 1,
      });
      const aGroup = new Group({
        account_id: this.account_id,
        club_id: this.id,
        name: "Advanced",
        index: 2,
      });

      try {
        await Promise.all([bGroup.save(), iGroup.save(), aGroup.save()]);
        this.groups.push(bGroup, iGroup, aGroup);
      } catch (e) {
        await this.groups.reload();
      }
    } else {
      let changed = false;
      if (this.groups.some((g) => isNil(g.color))) {
        changed = true;
        this.groups.forEach((g, i) => {
          g.color = Color(defaultColors[i % defaultColors.length]).rgbNumber();
        });
      }
      if (this.groups.some((g) => isNil(g.initial))) {
        changed = true;
        this.groups.forEach((g, i) => {
          if (g.name) g.initial = g.name[0].toUpperCase();
        });
      }
      if (changed) await this.groups.save();
    }

    if (this.groups.length === 0) {
      throw new Error("Failed to create groups");
    }
  }

  async startSession() {
    const night = await Night.createAndSetup({
      account_id: this.account_id,
      club_id: this.id,
    });
    if (!night) throw new Error("Failed to create night");
    return night.token!;
  }

  override async create() {
    const result = await super.create();
    await this.ensureGroups();
    return result;
  }

  /**
   * Returns a map of group IDs to an array of group IDs that the group excludes
   */
  get groupExclusions(): Record<string, string[]> {
    const groupExclusions: Record<string, Set<string>> = {};
    const rules = this.fill_rules;

    for (const rule of rules) {
      if (rule.subjectType !== "Group") continue;
      if (!rule.group_id) continue;
      if (!rule.rule_type) continue;

      const ruleType = rule.rule_type as RuleType;

      switch (ruleType) {
        case "never": {
          if (!rule.group_ids) continue;

          if (!groupExclusions[rule.group_id]) {
            groupExclusions[rule.group_id] = new Set();
          }

          for (const groupId of rule.group_ids) {
            groupExclusions[rule.group_id].add(groupId);
          }
          break;
        }
        case "prefers":
        case "only": {
          if (!rule.group_ids) continue;

          if (!groupExclusions[rule.group_id]) {
            groupExclusions[rule.group_id] = new Set();
          }

          for (const groupId of this.groups.map((g) => g.id!)) {
            if (!rule.group_ids.includes(groupId)) {
              groupExclusions[rule.group_id].add(groupId);
            }
          }
          break;
        }
      }
    }

    makeRecordSetRelationshipsBidirectional(groupExclusions);
    return Object.fromEntries(Object.entries(groupExclusions).map(([k, v]) => [k, Array.from(v)]));
  }

  clubPermissions() {
    return AccountUser.current?.clubPermissionFor(this.id!);
  }
  hasPermission(permission: PermissionKey) {
    const clubPermission = this.clubPermissions();
    const hasPermission = clubPermission?.[permission] ?? false;
    return hasPermission;
  }
  hasAdminPermission() {
    return this.hasPermission("admin_club");
  }
  hasManagePermission() {
    return this.hasPermission("manage_club");
  }
  hasSessionPermission() {
    return this.hasPermission("manage_sessions");
  }
  hasViewPermission() {
    return this.hasPermission("view_club");
  }
  hasFundingPermission() {
    return this.hasPermission("manage_funding");
  }

  getPreference<T extends keyof ClubPreferences>(
    key: T,
    defaultValue?: ClubPreferences[T],
  ): ClubPreferences[T] | undefined {
    return this.preferences?.[key] ?? defaultValue;
  }

  setPreference<T extends keyof ClubPreferences>(key: T, value: ClubPreferences[T]): void {
    if (!this.preferences) this.preferences = {};
    this.preferences[key] = value;
  }

  getMemberRequirement<T extends keyof NonNullable<ClubPreferences["memberRequirements"]>>(
    key: T,
    defaultValue: NonNullable<ClubPreferences["memberRequirements"]>[T],
  ) {
    return this.preferences?.memberRequirements?.[key] ?? defaultValue;
  }

  setMemberRequirement<T extends keyof NonNullable<ClubPreferences["memberRequirements"]>>(
    key: T,
    value: NonNullable<ClubPreferences["memberRequirements"]>[T],
  ) {
    if (!this.preferences) this.preferences = {};
    if (!this.preferences.memberRequirements) this.preferences.memberRequirements = {};
    this.preferences.memberRequirements[key] = value;
  }

  override save(): Promise<void> {
    clearMemoized(this);
    return super.save();
  }

  @memoize
  get memberTypes(): ClubPlayerMemberType[] {
    const types: ClubPlayerMemberType[] = [];
    // Account must have members feature to show anything other than casual players
    if (!Account.current?.hasFeature("member_players")) {
      return ["casual"];
    }

    if (this.getPreference("hasCasual", true)) {
      types.push("casual");
    }

    if (this.getPreference("hasMembers")) {
      types.push("member");
    }

    return types;
  }
}
export default Club;
